import { BadgeCheck, Bell, ChevronLeft, Monitor } from 'lucide-react';
import { useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router';

import { BrandLogo } from '@/components/brand/BrandLogo';
import { Button } from '@/components/ui/button';
import { ROUTES } from '@/config/routes';
import { NotificationSettingsDialog } from '@/features/notification/components/NotificationSettingsDialog';
import { WorkspaceYearSync } from '@/features/program-year/components/WorkspaceYearSync';
import { SubscriptionExpiryBanner } from '@/features/subscription/components/SubscriptionExpiryBanner';
import { useFeature } from '@/hooks/useFeature';
import { useRouteHandle } from '@/hooks/useRouteHandle';
import { cn } from '@/lib/utils';

import { useMobileTabs } from '../config/mobileTabs';
import { useMobileBack } from '../hooks/useMobileBack';
import { useSwitchToPc } from '../hooks/useSwitchToPc';

const M = ROUTES.workspace.mobile;

/**
 * 모바일 화면 셸(specs/037·#204) — 상단바(뒤로·제목·알림 설정·PC 버전) + 구독 만료·임박 배너 + 본문 + 하단 탭 4개. 첫 화면(공고 검색)에서는 뒤로
 * 대신 브랜드 로고를 보인다. 헤더 구성(h-14·border-b·아이콘 버튼 + 제목)은
 * Admin Kit `ai-mobile-preview-screen` 헤더를 참고했다(원본에 모바일 하단 탭 패턴은 없음).
 */
export function MobileLayout() {
  const { pathname } = useLocation();
  const { title } = useRouteHandle();
  const switchToPc = useSwitchToPc();
  const goBack = useMobileBack();
  const tabs = useMobileTabs();
  const firstTab = tabs[0]?.to ?? M.root;
  const atHome = pathname === firstTab;
  const showCertification = useFeature('CERTIFICATION');
  const [notifyOpen, setNotifyOpen] = useState(false);

  return (
    <div className="flex min-h-screen flex-col bg-background">
      {/* 기준 연도 ↔ 주소·기억 동기화(specs/032) — PC 레이아웃과 같은 연도 기준으로 검색·판정한다 */}
      <WorkspaceYearSync />
      <header className="sticky top-0 z-30 flex h-14 shrink-0 items-center gap-2 border-b bg-card px-2">
        {atHome ? (
          // 좁은 화면(390px·글자 배율 120%)에서 로고가 줄어야 우측 버튼이 화면 밖으로 밀리지 않는다
          <span className="min-w-0 shrink overflow-hidden px-2">
            <BrandLogo />
          </span>
        ) : (
          <Button variant="ghost" size="icon" aria-label="뒤로 가기" onClick={goBack}>
            <ChevronLeft />
          </Button>
        )}
        <h1 className="min-w-0 flex-1 truncate text-base font-semibold">{atHome ? '' : title}</h1>
        {showCertification && (
          <Button variant="ghost" size="icon" asChild>
            <Link to={M.certifications} aria-label="기업 인증 안내">
              <BadgeCheck aria-hidden />
            </Link>
          </Button>
        )}
        {/* 알림 설정(specs/081) — 모바일 중심 회원(기업 회원)이 PC 로 넘어가지 않고 끄고 켠다 */}
        <Button
          variant="ghost"
          size="icon"
          aria-label="알림 설정"
          title="알림 설정"
          onClick={() => setNotifyOpen(true)}
        >
          <Bell aria-hidden />
        </Button>
        <Button variant="ghost" size="sm" className="shrink-0" onClick={() => switchToPc()}>
          <Monitor />
          PC 버전
        </Button>
      </header>
      {notifyOpen && <NotificationSettingsDialog onClose={() => setNotifyOpen(false)} />}

      <main className="min-w-0 flex-1 space-y-4 px-4 pt-4 pb-24">
        {/* 홈 메뉴를 없앤 뒤에도 만료·임박 안내를 놓치지 않게 모든 탭 공통으로 둔다(#204). 해당 없으면 그리지 않는다. */}
        <SubscriptionExpiryBanner />
        <Outlet />
      </main>

      <nav
        aria-label="모바일 메뉴"
        className="fixed inset-x-0 bottom-0 z-30 flex border-t bg-card *:min-w-0 *:flex-1"
      >
        {tabs.map(({ to, label, icon: Icon, active }) => {
          const current = active(pathname);
          return (
            <Link
              key={to}
              to={to}
              aria-current={current ? 'page' : undefined}
              className={cn(
                'flex min-h-16 flex-col items-center justify-center gap-1 text-xs font-medium transition-colors focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none',
                current ? 'text-primary' : 'text-muted-foreground',
              )}
            >
              <Icon className="size-5" aria-hidden />
              {label}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
